<template>
  <section style="width: 100%">
    <v-alert
      v-if="extTrainingCategories && !extTrainingCategories.length"
      dense
      outlined
      icon="error"
      type="error"
      class="white-alert safety-elevation-3"
    >
      {{ $t("extTrainings.category.noData") }}
    </v-alert>
    <v-row align="center" no-gutters class="mb-3">
      <v-col cols="auto" class="pl-0">
        <div class="title text--secondary mt-2 px-4 text-uppercase">
          {{ $t("extTrainings.title") }}
        </div>
      </v-col>

      <v-col class="text-end">
        <change-ext-training-group
          :categories="extTrainingCategories"
          :extTrainingGroups="extTrainingGroups"
          @refreshGroups="$apollo.queries.extTrainingGroups.refresh()"
        />
        <change-ext-training-category
          :extTrainingCategories="extTrainingCategories"
          :extTrainingGroups="extTrainingGroups"
          @refreshCategories="$apollo.queries.extTrainingCategories.refresh()"
        />
        <v-btn
          v-if="
            role('admin', 'safetyRepresentative') &&
              extTrainingCategories &&
              extTrainingCategories.length
          "
          class="primary"
          @click="addExtTraining()"
          :loading="$apollo.loading"
        >
          <v-icon dark left>note_add</v-icon>
          {{ $t("extTrainings.add") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="extTrainings"
      :search="search"
      sort-by="dueDays"
      sort-desc
      fixed-header
      :loading="$apollo.loading"
      :footer-props="{
        'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
        'items-per-page-all-text': `${$t('employees.footer.all')}`,
      }"
      class="safety-elevation-3 table-striped"
    >
      <template v-slot:top>
        <v-row align="center" class="mx-4">
          <v-text-field
            class="flex-grow-1 pr-4 py-5"
            v-model="search"
            append-icon="search"
            :label="$t('search.title')"
            single-line
            hide-details
            solo
            :background-color="
              $vuetify.theme.themes.light.primary | hexToRgb(0.2)
            "
            dense
            flat
          />
          <v-btn
            :text="!showActive"
            color="primary"
            @click="handleShowActive(true)"
            >{{ $t("instructions.tabs.active") }}
          </v-btn>
          <v-btn
            :text="showActive"
            color="primary"
            @click="handleShowActive(false)"
            >{{ $t("extTrainings.archived") }}
          </v-btn>
        </v-row>
      </template>

      <template v-slot:[`item.groups`]="{ item }">
        <v-chip
          v-for="groupName in groupNamesByCategoryId.get(item.categoryId) || []"
          :key="groupName"
          small
        >
          {{ groupName }}</v-chip
        >
      </template>

      <template v-slot:[`item.durationMinutes`]="{ item }">
        {{ (item.durationMinutes * 60) | timeDuration }}
      </template>

      <template v-slot:[`item.dueDays`]="{ item }">
        {{ validUntilDaysText(item.dueDays, item.dueInMonths) }}
      </template>

      <template v-slot:[`item.dateTimeOfOccurrence`]="{ item }">
        {{ item.dateTimeOfOccurrence | dateTimeWithoutSeconds }} <br />
      </template>

      <template v-slot:[`item.employees`]="{ item }">
        {{
          `${item.employees.filter((e) => e.participated).length}/${
            item.employees.length
          }`
        }}
      </template>
      <template v-slot:no-results>{{
        $t("search.noResults", { item: search })
      }}</template>
      <template v-slot:[`item.menu`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="edit(item.id)">{{
              $t("extTrainings.editMetadata")
            }}</v-list-item>
            <v-list-item @click="editEmployees(item.id)">{{
              $t("extTrainings.employees.change")
            }}</v-list-item>
            <v-list-item v-if="item.isActive" @click="archive(item.id)">{{
              $t("extTrainings.archive")
            }}</v-list-item>
            <v-list-item v-if="!item.isActive" @click="activate(item.id)">{{
              $t("extTrainings.unarchive")
            }}</v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <ext-training-edit
      :extTraining="editExtTraining"
      @done="done"
      @created="(id) => extTrainingCreated(id)"
      :categories="extTrainingCategories"
    />
    <ext-training-employees-edit
      :extTraining="editExtTrainingEmployees"
      @done="done"
      @refresh="(id) => editEmployees(id)"
    />
  </section>
</template>

<script>
import { mapGetters } from "vuex";

import ExtTrainingEdit from "@/components/extTraining/ExtTrainingEdit";
import ExtTrainingEmployeesEdit from "@/components/extTraining/ExtTrainingEmployeesEdit";
import ChangeExtTrainingCategory from "@/components/extTraining/ChangeExtTrainingCategory";
import ChangeExtTrainingGroup from "@/components/extTraining/ChangeExtTrainingGroup";

import ExtTrainingsQuery from "@/graphql/queries/ExtTrainings.gql";
import EditExtTrainingQuery from "@/graphql/queries/EditExtTraining.gql";
import ExtTrainingCategoriesQuery from "@/graphql/queries/ExtTrainingCategories.gql";

import SetExtTrainingIsActive from "@/graphql/mutation/extTraining/SetExtTrainingIsActive.gql";
import ExtTrainingGroupsQuery from "@/graphql/queries/ExtTrainingGroups.gql";

export default {
  components: {
    ExtTrainingEdit,
    ExtTrainingEmployeesEdit,
    ChangeExtTrainingCategory,
    ChangeExtTrainingGroup,
  },
  data() {
    return {
      extTrainings: [],
      editExtTraining: null,
      editTest: null,
      search: "",
      editExtTrainingEmployees: null,
      uploading: false,
      showActive: true,
      page: 0,
    };
  },
  computed: {
    ...mapGetters(["role"]),

    headers() {
      const h = [
        {
          text: "ID",
          value: "id",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("extTrainings.column.category"),
          value: "category",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("extTrainings.column.groups"),
          value: "groups",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("extTrainings.column.name"),
          value: "name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.due"),
          value: "dueDays",
          class: "text-uppercase subheading text-truncate",
          sort: (a, b) => this.customSort(a, b),
        },
        {
          text: this.$i18n.t("extTrainings.column.date"),
          value: "dateTimeOfOccurrence",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t(`extTrainings.column.educator`),
          value: `educator`,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t(`extTrainings.column.location`),
          value: `location`,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t(`extTrainings.column.duration`),
          value: `durationMinutes`,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t(`extTrainings.column.employees`),
          value: `employees`,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: "",
          value: "menu",
          sortable: false,
          align: "right",
        },
      ];
      if (this.role("manager", "departmentManager")) {
        h.pop();
      }
      return h;
    },

    groupNamesByCategoryId() {
      const m = new Map();
      this.extTrainingGroups.forEach((etg) => {
        etg.extTrainingCategories.forEach((etc) => {
          if (!m.has(etc.id)) {
            m.set(etc.id, []);
          }
          const groupNames = m.get(etc.id);
          if (!groupNames.includes(etg.name)) {
            groupNames.push(etg.name);
          }
        });
      });
      return m;
    },
  },

  methods: {
    extTrainingCreated(id) {
      this.editExtTraining = null;
      this.editEmployees(id);
    },
    customSort(a, b) {
      if (Math.abs(a) < 90) a += 10000;
      if (Math.abs(b) < 90) b += 10000;

      return a - b;
    },

    activate(id) {
      if (isNaN(id)) {
        return;
      }
      this.setExtTrainingIsActive(id, true);
    },

    archive(id) {
      if (isNaN(id)) {
        return;
      }
      this.setExtTrainingIsActive(id, false);
    },

    setExtTrainingIsActive(id, state) {
      this.$apollo
        .mutate({
          mutation: SetExtTrainingIsActive,
          variables: {
            id,
            state,
          },
        })
        .then(() => {
          this.$apollo.queries.extTrainings.refresh();
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            text: `Failed to change state of ext training id ${id} to state ${state} with error: ${err}`,
            color: "error",
          })
        );
    },

    validUntilDaysText(dueDays, dueInMonths) {
      if (isNaN(dueDays) || dueInMonths === 0) return "-";

      return dueDays > 0
        ? this.$tc("extTrainings.dueInDaysOver", `${dueDays}`)
        : this.$tc("extTrainings.dueInDaysUntil", `${Math.abs(dueDays)}`);
    },

    addExtTraining() {
      this.editExtTraining = {
        categoryId: null,
        name: "",
        dateTimeOfOccurrence: new Date().toISOString(),
        educator: "",
        location: "",
        durationMinutes: 90,
        dueInMonths: null,
        employees: [],
        description: "",
      };
    },

    done() {
      this.editExtTrainingEmployees = null;
      this.editExtTraining = null;
      this.$apollo.queries.extTrainings.refresh();
    },

    editEmployees(id) {
      this.$apollo
        .query({
          query: EditExtTrainingQuery,
          variables: {
            id,
          },
        })
        .then((res) => {
          this.editExtTrainingEmployees = res.data.extTraining;
        });
    },
    edit(id) {
      this.$apollo
        .query({
          query: EditExtTrainingQuery,
          variables: {
            id,
          },
        })
        .then((res) => {
          const extTraining = res.data.extTraining;
          extTraining.noOverDue = extTraining.dueInMonths === 0;
          this.editExtTraining = res.data.extTraining;
        });
    },

    handleShowActive(showActive) {
      this.showActive = showActive;
      this.page = 0;
    },
  },

  apollo: {
    extTrainingGroups: {
      query: ExtTrainingGroupsQuery,
    },
    extTrainingCategories: {
      query: ExtTrainingCategoriesQuery,
    },
    extTrainings: {
      query: ExtTrainingsQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          showActive: this.showActive,
        };
      },
    },
  },
};
</script>

<style>
.theme--light.v-data-table .v-row-group__header,
.theme--light.v-data-table .v-row-group__summary {
  background: inherit !important;
}
.inactive {
  background: lightgrey !important;
}
</style>
